import React, { useState, useEffect, useContext } from 'react';
import { Modal, Table, Button, Space, Select, message, Popconfirm, Empty, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useGetUserBenefits, useAddUserBenefit, useRemoveUserBenefit } from '../../../hooks/user/userBenefitsDeductionsQuery';
import { useGetAllBenefits } from '../../../hooks/payrollData/payrollDataQuery';
import RefreshContext from '../../../context/refreshContext';

const { Option } = Select;

const UserBenefitsModal = ({ visible, onCancel, userId }) => {
  const [selectedBenefits, setSelectedBenefits] = useState([]);
  const { flag, setFlag } = useContext(RefreshContext);
  const [localRefresh, setLocalRefresh] = useState(false);

  const { benefits: availableBenefits } = useGetAllBenefits(flag);
  const { loading, benefits: userBenefits } = useGetUserBenefits(userId, localRefresh);
  const addUserBenefit = useAddUserBenefit();
  const removeUserBenefit = useRemoveUserBenefit();

  // Reset selected benefits when modal is opened
  useEffect(() => {
    if (visible) {
      setSelectedBenefits([]);
    }
  }, [visible]);

  const handleAddBenefit = async () => {
    if (!selectedBenefits.length) {
      message.error('Please select at least one benefit to add');
      return;
    }

    try {
      await addUserBenefit({
        userId,
        benefitIds: selectedBenefits
      });
      
      message.success('Benefits added successfully');
      setSelectedBenefits([]);
      setLocalRefresh(!localRefresh);
    } catch (error) {
      message.error('Failed to add benefits');
    }
  };

  const handleRemoveBenefit = async (benefitId) => {
    try {
      await removeUserBenefit(userId, benefitId);
      message.success('Benefit removed successfully');
      setLocalRefresh(!localRefresh);
    } catch (error) {
      message.error('Failed to remove benefit');
    }
  };

  // Filter out already assigned benefits from the dropdown
  const filteredBenefits = availableBenefits.filter(
    benefit => !userBenefits.some(userBenefit => userBenefit.benefit.id === benefit.id)
  );

  const columns = [
    {
      title: 'Benefit Name',
      dataIndex: ['benefit', 'benefitName'],
      key: 'benefitName'
    },
    {
      title: 'Value',
      dataIndex: ['benefit', 'benefitValue'],
      key: 'benefitValue',
      render: (text) => `₦${text.toLocaleString()}`
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to remove this benefit?"
          onConfirm={() => handleRemoveBenefit(record.benefit.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            Remove
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Modal
      title="Manage User Benefits"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <div style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%' }}>
          <Select
            mode="multiple"
            placeholder="Select benefits to add"
            style={{ width: 300 }}
            value={selectedBenefits}
            onChange={setSelectedBenefits}
            showSearch
            filterOption={(input, option) =>
              option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {filteredBenefits.map(benefit => (
              <Option key={benefit.id} value={benefit.id}>
                {benefit.benefitName} - ₦{benefit.benefitValue.toLocaleString()}
              </Option>
            ))}
          </Select>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddBenefit}
            disabled={!selectedBenefits.length}
          >
            Add Benefits
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {userBenefits.length > 0 ? (
          <Table
            columns={columns}
            dataSource={userBenefits}
            rowKey={(record) => record.id}
            pagination={{ pageSize: 5 }}
          />
        ) : (
          <Empty description="No benefits assigned to this user" />
        )}
      </Spin>
    </Modal>
  );
};

export default UserBenefitsModal;
